import type { ScreenerDiscoveryChanges, ScreenerDiscoveryExchangeDetail, ScreenerDiscoveryFilters, ScreenerDiscoveryItem } from '@/types'
import { createTickerSearchMatcher } from '@/lib/tickerSearch'

type OiMetrics = Pick<ScreenerDiscoveryExchangeDetail, 'oi_usd'> & ScreenerDiscoveryChanges
const finite = (value: number | null | undefined): value is number => value != null && Number.isFinite(value)

function meetsOiRules(item: OiMetrics, filters: ScreenerDiscoveryFilters): boolean {
  const period = filters.changePeriod ?? '24h'
  const pct = item[`oi_change_pct_${period}`]
  const usd = item[`oi_change_usd_${period}`]
  const coins = item[`oi_change_coins_${period}`]
  if (filters.minOiUsd > 0 && (!finite(item.oi_usd) || item.oi_usd < filters.minOiUsd)) return false
  if (filters.minAbsChangePct > 0 && (!finite(pct) || Math.abs(pct) < filters.minAbsChangePct)) return false
  if (filters.minAbsChangeUsd > 0 && (!finite(usd) || Math.abs(usd) < filters.minAbsChangeUsd)) return false
  const change = finite(coins) ? coins : pct
  if (filters.changeDirection === 'increase' && (!finite(change) || change <= 0)) return false
  if (filters.changeDirection === 'decrease' && (!finite(change) || change >= 0)) return false
  return true
}

/** Every enabled rule must pass on the same selected venue. */
export function getMatchingDiscoveryExchanges(item: ScreenerDiscoveryItem, filters: ScreenerDiscoveryFilters): ScreenerDiscoveryExchangeDetail[] {
  const selected = new Set(filters.exchanges.map((exchange) => exchange.trim().toLowerCase()))
  return (item.exchange_details ?? []).filter((detail) => selected.has(detail.exchange) && meetsOiRules(detail, filters))
}

function strongestChange(details: ScreenerDiscoveryExchangeDetail[], key: keyof ScreenerDiscoveryChanges): number | null {
  let strongest: number | null = null
  for (const detail of details) {
    const value = detail[key]
    if (finite(value) && (strongest === null || Math.abs(value) > Math.abs(strongest))) strongest = value
  }
  return strongest
}

/** Filter the complete exchange snapshot before limiting the displayed rows. */
export function filterDiscoveryItems(
  items: ScreenerDiscoveryItem[],
  filters: ScreenerDiscoveryFilters,
  limit = 500,
): ScreenerDiscoveryItem[] {
  const period = filters.changePeriod ?? '24h'
  const matchesSearch = createTickerSearchMatcher(filters.search, items.map((item) => item.symbol))
  const included = filters.includeTags.map((tag) => tag.trim().toLowerCase()).filter(Boolean)
  const excluded = filters.excludeTags.map((tag) => tag.trim().toLowerCase()).filter(Boolean)
  const matching: ScreenerDiscoveryItem[] = []
  for (const item of items) {
    const tags = item.tags.map((tag) => tag.toLowerCase())
    // Tag substrings such as AKE in OI_AVAILABLE_KRAKEN hide ticker matches
    // among unrelated rows. Tags are selected explicitly through the filters.
    if (!matchesSearch(item.symbol)) continue
    if (!included.every((tag) => tags.includes(tag)) || excluded.some((tag) => tags.includes(tag))) continue
    const details = getMatchingDiscoveryExchanges(item, filters)
    if (filters.filterScope === 'exchange') {
      if (details.length < Math.max(1, filters.minMatchingExchanges || 1)) continue
    } else if (!meetsOiRules(item, filters)) continue
    // The snapshot is shared by React Query: attach match metadata to new rows.
    matching.push({
      ...item,
      matching_exchanges: details.map((detail) => detail.exchange),
      matching_exchange_count: details.length,
      [`matching_change_pct_${period}`]: strongestChange(details, `oi_change_pct_${period}`),
      [`matching_change_usd_${period}`]: strongestChange(details, `oi_change_usd_${period}`),
    })
  }
  const direction = filters.sortDirection === 'asc' ? 1 : -1
  const compare = (left: string | number, right: string | number) => left < right ? -1 : left > right ? 1 : 0
  matching.sort((left, right) => {
    const magnitude = filters.sortBy.startsWith('matching_change_')
    const sortValue = (item: ScreenerDiscoveryItem) => {
      const value = item[filters.sortBy]
      if (filters.sortBy === 'last_price' && (!finite(item.last_price) || item.last_price <= 0)) return null
      if (typeof value === 'number' && !Number.isFinite(value)) return null
      return magnitude && typeof value === 'number' ? Math.abs(value) : value
    }
    const leftValue = sortValue(left)
    const rightValue = sortValue(right)
    // Unavailable values stay last in either direction, matching the API.
    if (leftValue == null && rightValue == null) return compare(left.symbol, right.symbol)
    if (leftValue == null) return 1
    if (rightValue == null) return -1
    return direction * (compare(leftValue, rightValue) || compare(left.symbol, right.symbol))
  })
  return matching.slice(0, limit)
}

export function discoveryExtraRuleCount(filters: ScreenerDiscoveryFilters): number {
  return [
    filters.minOiUsd > 0,
    filters.minAbsChangeUsd > 0,
    filters.filterScope === 'exchange' && filters.minMatchingExchanges > 1,
    filters.includeTags.length > 0,
    filters.excludeTags.length > 0,
  ].filter(Boolean).length
}

export function discoveryFilterSummary(filters: ScreenerDiscoveryFilters): string {
  const scope = filters.filterScope === 'exchange' ? 'Per exchange' : 'Combined'
  const direction = filters.changeDirection === 'increase' ? 'Increase' : filters.changeDirection === 'decrease' ? 'Decrease' : 'Either direction'
  return `${scope} · ${filters.changePeriod ?? '24h'} · ${direction}${filters.minAbsChangePct > 0 ? ` · ≥${filters.minAbsChangePct}%` : ''}`
}
